import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Home, Clock, LineChart, CalendarRange,
  Boxes, RefreshCcw, Upload, LogOut, Package, Bot, Calculator
} from 'lucide-react';

const apiUrl = process.env.REACT_APP_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:8000`;

const REQUIRED_FILE_SLOTS = [
  { key: 'nomenclature', label: 'Номенклатура' },
  { key: 'multiplicity', label: 'Кратність' },
  { key: 'sales_borei', label: 'Продажі Борей' },
  { key: 'sales_torest', label: 'Продажі ТоРест' },
  { key: 'sales_fop', label: 'Продажі ФОП' },
  { key: 'purchases_borei', label: 'Закупка Борей' },
  { key: 'purchases_torest', label: 'Закупка ТоРест' },
  { key: 'purchases_fop', label: 'Закупка ФОП' },
  { key: 'stock', label: 'Залишки' },
];

function Sidebar() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [calculateStatus, setCalculateStatus] = useState('');
  
  // Server-side upload status (do NOT rely on localStorage)
  const [uploadState, setUploadState] = useState({});

  const fetchUploadStatus = async () => {
    try {
      const res = await fetch(`${apiUrl}/file/upload_status/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setUploadState(data || {});
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    fetchUploadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allRequiredUploaded = REQUIRED_FILE_SLOTS.every(s => uploadState?.[s.key]?.uploaded);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const handleUpload = () => {
    if (!file || !fileType) {
      setUploadStatus('⚠️ Оберіть файл та тип');
      setTimeout(() => setUploadStatus(''), 3000);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    // Визначаємо endpoint в залежності від типу файлу
    let endpoint;
    if (fileType === 'nomenclature') {
      endpoint = '/file/load_nomenclature/';
    } else if (fileType === 'multiplicity') {
      endpoint = '/file/load_multiplicity/';
    } else {
      endpoint = '/file/load_exel/';  // Для Продажі, Закупівлі, Залишки - backend сам визначить тип
    }

    const fullUrl = `${apiUrl}${endpoint}`;
    console.log(`📤 Завантаження файлу: ${file.name}`);
    console.log(`📍 URL: ${fullUrl}`);
    console.log(`🔑 Token: ${localStorage.getItem('authToken') ? 'Є' : 'НЕМАЄ'}`);

    setUploading(true);
    fetch(`${apiUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: formData
    })
      .then(async (res) => {
        console.log(`📊 Response status: ${res.status} ${res.statusText}`);
        setUploading(false);
        if (res.ok) {
          const result = await res.json();
          console.log('✅ Завантаження успішне:', result);
          setUploadStatus('✅ Завантаження успішне');

          // Refresh server-side status after upload
          await fetchUploadStatus();
          
          setFile(null);
          setFileType('');
          // Очищаємо input
          const fileInput = document.querySelector('input[type="file"]');
          if (fileInput) fileInput.value = '';
        } else {
          const errorText = await res.text();
          console.error(`❌ Upload error [${res.status}]:`, errorText);
          setUploadStatus(`❌ Помилка ${res.status}: ${errorText.substring(0, 50)}`);
        }
        setTimeout(() => setUploadStatus(''), 8000);
      })
      .catch((error) => {
        console.error('❌ Upload failed (network):', error);
        console.error('Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
        setUploadStatus(`❌ Помилка: ${error.message}`);
        setTimeout(() => setUploadStatus(''), 8000);
        setUploading(false);
      });
  };

  const handleResetUploads = async () => {
    try {
      await fetch(`${apiUrl}/file/upload_status/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
    } finally {
      await fetchUploadStatus();
      setCalculateStatus('');
      setUploadStatus('✅ Можна завантажувати з нуля');
      setTimeout(() => setUploadStatus(''), 4000);
    }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    setCalculateStatus('⏳ Розраховуємо прогнози...');
    
    try {
      // 1. Розрахунок прогнозів по товарам (агреговано)
      console.log('📊 Розрахунок прогнозів по товарам...');
      const productResponse = await fetch(`${apiUrl}/analytics/product_arima_forecast`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      
      if (!productResponse.ok) {
        throw new Error(`Помилка розрахунку прогнозів по товарам: ${productResponse.status}`);
      }
      
      const productResult = await productResponse.json();
      console.log('✅ Прогнози по товарам розраховано:', productResult);
      
      // 2. Розрахунок прогнозів по складам
      console.log('📊 Розрахунок прогнозів по складам...');
      const warehouseResponse = await fetch(`${apiUrl}/analytics/arima_forecast`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      
      if (!warehouseResponse.ok) {
        throw new Error(`Помилка розрахунку прогнозів по складам: ${warehouseResponse.status}`);
      }
      
      const warehouseResult = await warehouseResponse.json();
      console.log('✅ Прогнози по складам розраховано:', warehouseResult);
      
      setCalculateStatus('✅ Розрахунки завершено!');
      
      setTimeout(() => {
        setCalculateStatus('');
        // Перезавантажити сторінку щоб оновити dashboard
        window.location.reload();
      }, 2000);

      // After successful calculation, allow starting from scratch (server-side reset of upload progress)
      await handleResetUploads();
      
    } catch (error) {
      console.error('❌ Помилка розрахунків:', error);
      setCalculateStatus(`❌ Помилка: ${error.message}`);
      setTimeout(() => setCalculateStatus(''), 8000);
    } finally {
      setCalculating(false);
    }
  };

  const navLinkStyle = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2 rounded text-left w-full ${
      isActive ? 'bg-blue-100 text-blue-600 font-semibold' : 'text-gray-700 hover:bg-gray-100'
    }`;

  return (
    <aside className="min-w-[12rem] bg-white shadow-md flex flex-col justify-between pt-8">
      <nav className="p-4 space-y-4">
        <h1 className="text-xl font-bold mb-6 ml-1">CleanPoint</h1>

        <NavLink to="/dashboard" className={navLinkStyle}>
          <Home size={16} /> Головна
        </NavLink>

        <NavLink to="/ai-agent" className={navLinkStyle}>
          <Bot size={16} /> ШІ Агент
        </NavLink>

        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-500 uppercase px-4 mb-2">По складам</p>
          <NavLink to="/history" className={navLinkStyle}>
            <Clock size={16} /> Продажі
          </NavLink>
          <NavLink to="/forecasts" className={navLinkStyle}>
            <LineChart size={16} /> Прогноз
          </NavLink>
          <NavLink to="/leftovers" className={navLinkStyle}>
            <Boxes size={16} /> Залишки
          </NavLink>
        </div>

        <div className="space-y-1 mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase px-4 mb-2">По товарам</p>
          <NavLink to="/product-sales" className={navLinkStyle}>
            <Clock size={16} /> Продажі
          </NavLink>
          <NavLink to="/product-forecasts" className={navLinkStyle}>
            <LineChart size={16} /> Прогноз
          </NavLink>
          <NavLink to="/product-stock" className={navLinkStyle}>
            <Package size={16} /> Залишки
          </NavLink>
        </div>

        <div className="border-t pt-4 mt-4">
          <NavLink to="/seasonal" className={navLinkStyle}>
            <CalendarRange size={16} /> Сезонність
          </NavLink>
        </div>

        <NavLink to="/cron" className={navLinkStyle}>
          <RefreshCcw size={16} /> Оновлення
        </NavLink>

        <div className="mt-6 space-y-3">
          <label className="block text-gray-700 mb-1 flex items-center gap-2">
            <Upload size={16} /> Додати файл
          </label>
          
          {/* Dropdown для вибору типу файлу */}
          <select
            value={fileType}
            onChange={(e) => setFileType(e.target.value)}
            disabled={uploading}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded
              focus:outline-none focus:ring-2 focus:ring-blue-500
              disabled:opacity-50 disabled:cursor-not-allowed bg-white"
          >
            <option value="">-- Оберіть тип файлу --</option>
            <option value="nomenclature">📦 Номенклатура (.xls)</option>
            <option value="multiplicity">📏 Кратність (.xls)</option>
            <option value="sales">💰 Продажі (.xlsx/.xls)</option>
            <option value="purchases">🛒 Закупівлі (.xlsx/.xls)</option>
            <option value="stock">📊 Залишки (.xlsx)</option>
          </select>
          
          {/* Input для файлу */}
          <input
            type="file"
            accept=".xlsx,.xls"
            className="block w-full text-sm
              file:w-full file:py-2 file:px-4
              file:rounded file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100
              disabled:opacity-50"
            onChange={(e) => setFile(e.target.files[0])}
            disabled={uploading}
          />
          
          {file && (
            <p className="text-xs text-gray-600">
              📄 <strong>{file.name}</strong>
            </p>
          )}
          
          {/* Кнопка завантаження */}
          <button
            onClick={handleUpload}
            disabled={!file || !fileType || uploading}
            className="w-full bg-blue-500 
              hover:bg-blue-600 text-white py-2 
              px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed
              font-medium transition-colors"
          >
            {uploading ? '⏳ Завантаження...' : '📤 Завантажити'}
          </button>
          
          {uploadStatus && (
            <p className="text-xs text-center font-medium">
              {uploadStatus}
            </p>
          )}
          
          {/* Індикатор завантажених файлів */}
          <div className="text-xs text-gray-600 space-y-1 pt-2">
            {REQUIRED_FILE_SLOTS.map(slot => {
              const uploaded = uploadState?.[slot.key]?.uploaded;
              const filename = uploadState?.[slot.key]?.filename;
              return (
                <div key={slot.key} className="flex items-center gap-2">
                  <span>{uploaded ? '✅' : '⬜'}</span>
                  <span className={uploaded ? 'text-green-700 font-semibold' : ''}>{slot.label}</span>
                  {uploaded && filename ? (
                    <span className="text-[10px] text-gray-500 truncate max-w-[110px]" title={filename}>
                      ({filename})
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
          
          {/* Кнопка розрахунків */}
          <button
            onClick={handleCalculate}
            disabled={
              !allRequiredUploaded ||
              calculating
            }
            className="w-full bg-green-500 
              hover:bg-green-600 text-white py-2 
              px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed
              font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Calculator size={16} />
            {calculating ? '⏳ Розрахунок...' : '🧮 Провести розрахунки'}
          </button>

          <button
            onClick={handleResetUploads}
            disabled={uploading || calculating}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded
              disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            Почати з нуля
          </button>
          
          {calculateStatus && (
            <p className="text-xs text-center font-medium">
              {calculateStatus}
            </p>
          )}
        </div>

        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-2 bg-gray-500 text-white px-4 py-2 rounded w-full hover:bg-gray-600"
        >
          <LogOut size={16} /> Вийти
        </button>
      </nav>
    </aside>
  );
}

export default Sidebar;
